'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketId } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { Avatar } from '@morpho-org/ui-v6/client';
import { CardText, Skeleton, Tag } from '@morpho-org/ui-v6/server';
import { formatters, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { Icon, Separator, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui';
import { Alert, ArrowRight } from '@repo/ui/icons';
import { memo, type ReactNode } from 'react';
import { ProtectedLltv, ProtectedLltvLabel } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { LinkIcon } from '@/components/common/LinkIcon';
import { SimulationEstimateBadge } from '@/components/common/SimulationEstimateBadge';
import { useMarketSummaryData } from '@/components/MarketPage/MarketSimpleActionPanel/MarketSimpleActionPanelClient/MarketOperationPanel/MarketSummary/hooks/useMarketSummaryData';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketUrl } from '@/helpers/routing';
import { RecapContainer } from './RecapContainer';
import RecapInteraction from './RecapInteraction';

/** A position metric shown before → after (collateral, loan, LTV). */
export interface MarketRecapValues {
  collateral: bigint;
  borrowAssets: bigint;
  /** WAD loan-to-value. */
  ltv: bigint;
}
export interface MarketRecapV2Props {
  marketId: MarketId;
  chainId: ChainId;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  /** Which leg labels to use for the interaction rows (`Supply`/`Borrow` vs `Withdraw`/`Repay`). */
  mode: 'supplyBorrow' | 'repayWithdraw';
  /** Collateral amount being transacted (supplied or withdrawn). */
  collateralAmount: bigint;
  /** Loan amount being transacted (borrowed or repaid). */
  loanAmount: bigint;
  initial: MarketRecapValues;
  /** Position-after; `undefined` fields while the simulation resolves. */
  final: Partial<MarketRecapValues> | undefined;
  /** Market liquidation LTV (WAD) — static, shown as "Liq. LTV". */
  lltv: bigint;
  /** APY + rewards source (same as the panel summary card), for the Rate row. */
  marketForRewards?: MarketForRewards | null;
  avgBorrowApy?: number | null;
  /** Skeleton in place of the before-values while the current on-chain position loads. */
  isInitialPositionLoading?: boolean;
  /** Skeleton in place of the after-values while the EVM simulation resolves. */
  isFinalPositionLoading?: boolean;
  /** After-values are a JS-math estimate (simulation unavailable/unverified). */
  isSimulationFallback?: boolean;
}
const formatLtv = (ltv: bigint | undefined) => isDefined(ltv) ? formatters.percent.digits(2).unit('%').of(ltv, 18) : '-';
const COLLATERAL_OPERATION_LABEL = {
  supplyBorrow: 'Supply Collateral',
  repayWithdraw: 'Withdraw Collateral'
} as const;
const LOAN_OPERATION_LABEL = {
  supplyBorrow: 'Borrow',
  repayWithdraw: 'Repay'
} as const;

/** One before → after row (tooltip-detailed values, skeleton while loading, estimate badge on fallback).
 * Shared with the lend-withdraw recap (`MarketLendWithdrawRecapV2`). */
export const RecapPositionRow = t0 => {
  const $ = _c(23);
  const {
    label,
    before,
    beforeTooltip,
    after,
    afterTooltip,
    showAfter,
    isInitialPositionLoading,
    isFinalPositionLoading,
    showEstimateBadge,
    beforeColorClass: t1,
    afterColorClass: t2,
    adornment
  } = t0;
  const beforeColorClass = t1 === undefined ? "text-muted-foreground" : t1;
  const afterColorClass = t2 === undefined ? "text-foreground" : t2;
  let t3;
  if ($[0] !== label) {
    t3 = <span className="text-secondary-foreground text-xs">{label}</span>;
    $[0] = label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== before || $[3] !== beforeColorClass || $[4] !== beforeTooltip || $[5] !== isInitialPositionLoading || $[6] !== showAfter) {
    t4 = isInitialPositionLoading ? <Skeleton className="h-(--text-xs--line-height) w-[56px]" /> : <Tooltip><TooltipTrigger asChild={true}><span className={`text-xs ${showAfter ? beforeColorClass : "text-foreground"}`}>{before}</span></TooltipTrigger>{!!beforeTooltip && <TooltipContent>{beforeTooltip}</TooltipContent>}</Tooltip>;
    $[2] = before;
    $[3] = beforeColorClass;
    $[4] = beforeTooltip;
    $[5] = isInitialPositionLoading;
    $[6] = showAfter;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== after || $[9] !== afterColorClass || $[10] !== afterTooltip || $[11] !== isFinalPositionLoading || $[12] !== isInitialPositionLoading || $[13] !== showAfter || $[14] !== showEstimateBadge) {
    t5 = !isInitialPositionLoading && showAfter && <><Icon className="text-foreground" icon={<ArrowRight />} />{isFinalPositionLoading ? <Skeleton className="h-(--text-xs--line-height) w-[56px]" /> : <Tooltip><TooltipTrigger asChild={true}><span className={`text-xs ${afterColorClass}`}>{after}</span></TooltipTrigger>{!!afterTooltip && <TooltipContent>{afterTooltip}</TooltipContent>}</Tooltip>}{showEstimateBadge && !isFinalPositionLoading && <SimulationEstimateBadge />}</>;
    $[8] = after;
    $[9] = afterColorClass;
    $[10] = afterTooltip;
    $[11] = isFinalPositionLoading;
    $[12] = isInitialPositionLoading;
    $[13] = showAfter;
    $[14] = showEstimateBadge;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== adornment || $[17] !== t4 || $[18] !== t5) {
    t6 = <div className="flex items-center gap-[2px]">{t4}{t5}{adornment}</div>;
    $[16] = adornment;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t3 || $[21] !== t6) {
    t7 = <div className="flex items-center justify-between gap-[16px]">{t3}{t6}</div>;
    $[20] = t3;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};

/**
 * Market-actions-v2 recap (sister of `VaultV2Recap`). Mirrors the legacy
 * simulation-sdk market recap (`MarketRecapInner`) element-for-element — market
 * header (avatars + name + Liq. LTV tag + open-market link), the operation
 * interaction rows (Supply/Borrow or Withdraw/Repay), and the Collateral / Loan /
 * LTV before→after rows plus the Rate (net APY + rewards) and Liq. LTV — but is
 * fed by `useMarketSimulatedFinalPosition` (evm-simulation) rather than the
 * sandbox bundler. Position figures + APY/rewards reuse `useMarketSummaryData`,
 * the same hook that powers the panel summary card, so the two stay consistent.
 */
const MarketRecapV2 = t0 => {
  const $ = _c(142);
  const {
    marketId,
    chainId,
    collateralAsset,
    loanAsset,
    mode,
    collateralAmount,
    loanAmount,
    initial,
    final,
    lltv,
    marketForRewards,
    avgBorrowApy,
    isInitialPositionLoading,
    isFinalPositionLoading,
    isSimulationFallback
  } = t0;
  const {
    targetChainIdentifier
  } = useWeb3Context();
  let t1;
  if ($[0] !== initial.borrowAssets || $[1] !== initial.collateral || $[2] !== initial.ltv) {
    t1 = {
      collateral: initial.collateral,
      loan: initial.borrowAssets,
      ltv: initial.ltv
    };
    $[0] = initial.borrowAssets;
    $[1] = initial.collateral;
    $[2] = initial.ltv;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = final?.collateral;
  const t3 = final?.borrowAssets;
  const t4 = final?.ltv;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      collateral: t2,
      loan: t3,
      ltv: t4
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== avgBorrowApy || $[9] !== chainId || $[10] !== collateralAsset || $[11] !== lltv || $[12] !== loanAsset || $[13] !== marketForRewards || $[14] !== marketId || $[15] !== t1 || $[16] !== t5) {
    t6 = {
      marketId,
      chainId,
      initialData: t1,
      finalData: t5,
      collateralAsset,
      loanAsset,
      lltv,
      avgBorrowApy,
      marketForRewards
    };
    $[8] = avgBorrowApy;
    $[9] = chainId;
    $[10] = collateralAsset;
    $[11] = lltv;
    $[12] = loanAsset;
    $[13] = marketForRewards;
    $[14] = marketId;
    $[15] = t1;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const {
    collateral,
    loan,
    ltv,
    apy: apyData
  } = useMarketSummaryData(t6);
  if (!collateralAsset || !loanAsset) {
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CardText height="240px" text="Cannot display market interaction" />;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  let t7;
  if ($[19] !== loanAsset.logoURI) {
    t7 = <Avatar aria-hidden={true} src={loanAsset.logoURI} size="medium" />;
    $[19] = loanAsset.logoURI;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = loanAsset.symbol || UNKNOWN_SYMBOL;
  let t9;
  if ($[21] !== loanAsset.symbol) {
    t9 = ref => <span ref={ref} className="text-title-xs text-foreground max-w-[160px] min-w-0 truncate md:max-w-none">{loanAsset.symbol || UNKNOWN_SYMBOL}</span>;
    $[21] = loanAsset.symbol;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <TruncatedTooltip text={t8}>{t9}</TruncatedTooltip>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t7) {
    t11 = <span className="gap-xs inline-flex min-w-0 items-center">{t7}{t10}</span>;
    $[26] = t10;
    $[27] = t7;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span aria-hidden={true} className="bg-muted-foreground h-lg w-0.5 shrink-0 rounded-full" />;
    t13 = <span className="sr-only">backed by</span>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t12 = $[29];
    t13 = $[30];
  }
  let t14;
  if ($[31] !== collateralAsset.logoURI) {
    t14 = <Avatar aria-hidden={true} src={collateralAsset.logoURI} size="medium" />;
    $[31] = collateralAsset.logoURI;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = collateralAsset.symbol || UNKNOWN_SYMBOL;
  let t16;
  if ($[33] !== collateralAsset.symbol) {
    t16 = ref_0 => <span ref={ref_0} className="text-title-xs text-foreground max-w-[160px] min-w-0 truncate md:max-w-none">{collateralAsset.symbol || UNKNOWN_SYMBOL}</span>;
    $[33] = collateralAsset.symbol;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <TruncatedTooltip text={t15}>{t16}</TruncatedTooltip>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      lineHeight: "normal"
    };
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== lltv) {
    t19 = formatLtv(lltv);
    $[39] = lltv;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t19) {
    t20 = <Tag variant="bundler"><span className="text-3xs text-foreground" style={t18}>{t19}</span></Tag>;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t14 || $[44] !== t17 || $[45] !== t20) {
    t21 = <span className="gap-xs inline-flex min-w-0 items-center">{t12}{t13}<span className="gap-xs inline-flex min-w-0 items-center">{t14}{t17}{t20}</span></span>;
    $[43] = t14;
    $[44] = t17;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = collateralAsset.symbol ?? "";
  const t23 = loanAsset.symbol ?? "";
  let t24;
  if ($[47] !== marketId || $[48] !== t22 || $[49] !== t23 || $[50] !== targetChainIdentifier) {
    t24 = getMarketUrl({
      identifier: targetChainIdentifier,
      marketId,
      collateralSymbol: t22,
      loanSymbol: t23
    });
    $[47] = marketId;
    $[48] = t22;
    $[49] = t23;
    $[50] = targetChainIdentifier;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t24) {
    t25 = <LinkIcon tooltip="Open market." href={t24} />;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t11 || $[55] !== t21 || $[56] !== t25) {
    t26 = <div className="gap-xs flex flex-wrap items-center">{t11}{t21}{t25}</div>;
    $[54] = t11;
    $[55] = t21;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== collateralAmount || $[59] !== collateralAsset || $[60] !== mode) {
    t27 = collateralAmount > ZERO && <><RecapInteraction amount={collateralAmount} asset={collateralAsset} label={COLLATERAL_OPERATION_LABEL[mode]} /><Separator /></>;
    $[58] = collateralAmount;
    $[59] = collateralAsset;
    $[60] = mode;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== loanAmount || $[63] !== loanAsset || $[64] !== mode) {
    t28 = loanAmount > ZERO && <><RecapInteraction amount={loanAmount} asset={loanAsset} label={LOAN_OPERATION_LABEL[mode]} /><Separator /></>;
    $[62] = loanAmount;
    $[63] = loanAsset;
    $[64] = mode;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const t29 = `Loan (${loanAsset.symbol})`;
  let t30;
  if ($[66] !== isFinalPositionLoading || $[67] !== isInitialPositionLoading || $[68] !== isSimulationFallback || $[69] !== loan.final || $[70] !== loan.finalDetailed || $[71] !== loan.initial || $[72] !== loan.initialDetailed || $[73] !== loan.showFinalValue || $[74] !== t29) {
    t30 = <RecapPositionRow label={t29} before={loan.initial} beforeTooltip={loan.initialDetailed} after={loan.final} afterTooltip={loan.finalDetailed} showAfter={loan.showFinalValue} isInitialPositionLoading={isInitialPositionLoading} isFinalPositionLoading={isFinalPositionLoading} showEstimateBadge={isSimulationFallback} />;
    $[66] = isFinalPositionLoading;
    $[67] = isInitialPositionLoading;
    $[68] = isSimulationFallback;
    $[69] = loan.final;
    $[70] = loan.finalDetailed;
    $[71] = loan.initial;
    $[72] = loan.initialDetailed;
    $[73] = loan.showFinalValue;
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  const t31 = `Collateral (${collateralAsset.symbol})`;
  let t32;
  if ($[76] !== collateral.final || $[77] !== collateral.finalDetailed || $[78] !== collateral.initial || $[79] !== collateral.initialDetailed || $[80] !== collateral.showFinalValue || $[81] !== isFinalPositionLoading || $[82] !== isInitialPositionLoading || $[83] !== isSimulationFallback || $[84] !== t31) {
    t32 = <RecapPositionRow label={t31} before={collateral.initial} beforeTooltip={collateral.initialDetailed} after={collateral.final} afterTooltip={collateral.finalDetailed} showAfter={collateral.showFinalValue} isInitialPositionLoading={isInitialPositionLoading} isFinalPositionLoading={isFinalPositionLoading} showEstimateBadge={isSimulationFallback} />;
    $[76] = collateral.final;
    $[77] = collateral.finalDetailed;
    $[78] = collateral.initial;
    $[79] = collateral.initialDetailed;
    $[80] = collateral.showFinalValue;
    $[81] = isFinalPositionLoading;
    $[82] = isInitialPositionLoading;
    $[83] = isSimulationFallback;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  const t33 = ltv.isInitialUnhealthy ? "text-destructive-foreground" : undefined;
  const t34 = ltv.isFinalUnhealthy ? "text-destructive-foreground" : undefined;
  let t35;
  if ($[86] !== isFinalPositionLoading || $[87] !== ltv.isFinalUnhealthy || $[88] !== ltv.showFinalValue) {
    t35 = !isFinalPositionLoading && ltv.showFinalValue && ltv.isFinalUnhealthy ? <Tooltip><TooltipTrigger asChild={true}><Icon role="img" aria-label="Warning: position close to its threshold" className="text-destructive-foreground" icon={<Alert />} /></TooltipTrigger><TooltipContent>Your position is close to its threshold. If it's crossed, part or all of your collateral can be sold to repay your loan, at a loss.</TooltipContent></Tooltip> : undefined;
    $[86] = isFinalPositionLoading;
    $[87] = ltv.isFinalUnhealthy;
    $[88] = ltv.showFinalValue;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  let t36;
  if ($[90] !== isFinalPositionLoading || $[91] !== isInitialPositionLoading || $[92] !== isSimulationFallback || $[93] !== ltv.final || $[94] !== ltv.initial || $[95] !== ltv.showFinalValue || $[96] !== t33 || $[97] !== t34 || $[98] !== t35) {
    t36 = <RecapPositionRow label="LTV" before={ltv.initial} after={ltv.final} showAfter={ltv.showFinalValue} isInitialPositionLoading={isInitialPositionLoading} isFinalPositionLoading={isFinalPositionLoading} showEstimateBadge={isSimulationFallback} beforeColorClass={t33} afterColorClass={t34} adornment={t35} />;
    $[90] = isFinalPositionLoading;
    $[91] = isInitialPositionLoading;
    $[92] = isSimulationFallback;
    $[93] = ltv.final;
    $[94] = ltv.initial;
    $[95] = ltv.showFinalValue;
    $[96] = t33;
    $[97] = t34;
    $[98] = t35;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  let t37;
  if ($[100] !== chainId || $[101] !== marketId) {
    t37 = <span className="text-secondary-foreground text-xs"><ProtectedLltvLabel defaultLabel="Liquidation LTV" label="Auto-Deleverage LLTV" marketId={marketId} chainId={chainId} /></span>;
    $[100] = chainId;
    $[101] = marketId;
    $[102] = t37;
  } else {
    t37 = $[102];
  }
  let t38;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      lineHeight: "normal"
    };
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  let t39;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Skeleton className="h-[14px] w-[30px] rounded-lg" />;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  let t40;
  if ($[105] !== chainId || $[106] !== lltv || $[107] !== marketId) {
    t40 = <span className="text-foreground text-xs" style={t38}><ProtectedLltv lltv={lltv} marketId={marketId} chainId={chainId} skeleton={t39} /></span>;
    $[105] = chainId;
    $[106] = lltv;
    $[107] = marketId;
    $[108] = t40;
  } else {
    t40 = $[108];
  }
  let t41;
  if ($[109] !== t37 || $[110] !== t40) {
    t41 = <div className="flex items-center justify-between gap-[16px]">{t37}{t40}</div>;
    $[109] = t37;
    $[110] = t40;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  let t42;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span className="text-secondary-foreground text-xs">Rate</span>;
    $[112] = t42;
  } else {
    t42 = $[112];
  }
  let t43;
  if ($[113] !== apyData.apy || $[114] !== apyData.netApy || $[115] !== apyData.points || $[116] !== apyData.rewards || $[117] !== collateralAsset) {
    t43 = {
      apy: apyData.apy,
      rewards: apyData.rewards,
      points: apyData.points,
      variant: "market",
      netApy: apyData.netApy,
      asset: collateralAsset
    };
    $[113] = apyData.apy;
    $[114] = apyData.netApy;
    $[115] = apyData.points;
    $[116] = apyData.rewards;
    $[117] = collateralAsset;
    $[118] = t43;
  } else {
    t43 = $[118];
  }
  let t44;
  if ($[119] !== chainId || $[120] !== loanAsset.address || $[121] !== marketId) {
    t44 = {
      kind: "market",
      chainId,
      marketId,
      loanTokenAddress: loanAsset.address
    };
    $[119] = chainId;
    $[120] = loanAsset.address;
    $[121] = marketId;
    $[122] = t44;
  } else {
    t44 = $[122];
  }
  let t45;
  if ($[123] !== apyData.formattedNetApy) {
    t45 = <span className="text-foreground text-xs" data-testid="market-recap-rate">{apyData.formattedNetApy}</span>;
    $[123] = apyData.formattedNetApy;
    $[124] = t45;
  } else {
    t45 = $[124];
  }
  let t46;
  if ($[125] !== t43 || $[126] !== t44 || $[127] !== t45) {
    t46 = <div className="flex items-center justify-between gap-[16px]">{t42}<IconRewardHover RewardsHoverCardProps={t43} merklContext={t44} isReversed={true} iconSize={20}>{t45}</IconRewardHover></div>;
    $[125] = t43;
    $[126] = t44;
    $[127] = t45;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] !== t30 || $[130] !== t32 || $[131] !== t36 || $[132] !== t41 || $[133] !== t46) {
    t47 = <div className="gap-sm flex flex-col items-stretch">{t30}{t32}{t36}{t41}{t46}</div>;
    $[129] = t30;
    $[130] = t32;
    $[131] = t36;
    $[132] = t41;
    $[133] = t46;
    $[134] = t47;
  } else {
    t47 = $[134];
  }
  let t48;
  if ($[135] !== t27 || $[136] !== t28 || $[137] !== t47) {
    t48 = <div className="gap-sm flex flex-col items-stretch">{t27}{t28}{t47}</div>;
    $[135] = t27;
    $[136] = t28;
    $[137] = t47;
    $[138] = t48;
  } else {
    t48 = $[138];
  }
  let t49;
  if ($[139] !== t26 || $[140] !== t48) {
    t49 = <RecapContainer className="gap-lg w-full flex-col items-stretch">{t26}{t48}</RecapContainer>;
    $[139] = t26;
    $[140] = t48;
    $[141] = t49;
  } else {
    t49 = $[141];
  }
  return t49;
};
export default memo(MarketRecapV2);